import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, Inject, PLATFORM_ID } from '@angular/core';
import { Store } from '@ngrx/store';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { MessageService } from 'primeng/api';
import { loadCompanies, setAccountId } from './common/ui/company-context-select/company-context.actions';
import { KeycloakService } from 'keycloak-angular';
import { TranslateService } from '@ngx-translate/core';
import { SwUpdate } from '@angular/service-worker';
import { PrimeNG } from 'primeng/config';
import { EkoVersionService, setupKeycloakFrontChannelLogoutListener } from '@ekowitryna/ui';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
  providers: [MessageService]
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'bdo-webpanel';

  private _translate = inject(TranslateService);
  private config = inject(PrimeNG);
  private readonly gtmService = inject(GoogleTagManagerService);
  @Inject(DOCUMENT) private document = inject(DOCUMENT);
  private store = inject(Store);
  private swUpdate = inject(SwUpdate);
  private keycloak = inject(KeycloakService);
  private platformId = inject(PLATFORM_ID);
  private frontChannelLogoutCleanup?: () => void;

  constructor(
  ) {
    inject(EkoVersionService).init();
    
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe(event => {
        if (event.type === 'VERSION_READY') {
          this.swUpdate.activateUpdate().then(() => document.location.reload());
        }
        if (event.type === 'VERSION_INSTALLATION_FAILED') {
          navigator.serviceWorker.getRegistrations().then(registrations => {
            registrations.forEach(reg => reg.unregister());
          });
          caches.keys().then(keys => {
            keys.forEach(key => caches.delete(key));
          });
          document.location.reload();
        }
      });

      setInterval(() => {
        this.swUpdate.checkForUpdate();
      }, 30000);
    }

    // Set account ID before loading companies to detect account change
    const keycloakInstance = this.keycloak.getKeycloakInstance();
    const accountId = keycloakInstance?.tokenParsed?.sub;
    if (accountId) {
      this.store.dispatch(setAccountId({ accountId }));
    }

    this.store.dispatch(loadCompanies());
    this.setTranslations();

    this.gtmService.addGtmToDom();
    // this.loadTheme();
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.frontChannelLogoutCleanup = setupKeycloakFrontChannelLogoutListener(
        this.keycloak.getKeycloakInstance()
      );
    }
  }

  ngOnDestroy(): void {
    this.frontChannelLogoutCleanup?.();
  }

  loadTheme() {
    // Determine the theme based on user preference or default
    const theme = 'light'; // or 'dark', or from user settings

    // Create a new link element for the theme
    const themeLink = this.document.createElement('link');
    themeLink.id = 'app-theme';
    themeLink.rel = 'stylesheet';
    themeLink.href = `assets/themes/material/${theme}/theme.css`;

    // Append the new theme link at the end of the <head>
    this.document.head.appendChild(themeLink);
  }

  setTranslations() {
    this.config.setTranslation({
      // Podstawowe tłumaczenia
      accept: 'Tak',
      reject: 'Nie',
      // Tłumaczenia dla filtrów
      startsWith: 'Zaczyna się od',
      contains: 'Zawiera',
      notContains: 'Nie zawiera',
      endsWith: 'Kończy się na',
      equals: 'Równe',
      notEquals: 'Nie równe',
      noFilter: 'Brak filtra',
      lt: 'Mniej niż',
      lte: 'Mniej niż lub równe',
      gt: 'Więcej niż',
      gte: 'Więcej niż lub równe',
      is: 'Jest',
      isNot: 'Nie jest',
      before: 'Przed',
      after: 'Po',
      dateIs: 'Data to',
      dateIsNot: 'Data to nie',
      dateBefore: 'Data przed',
      dateAfter: 'Data po',
      clear: 'Wyczyść',
      apply: 'Zastosuj',
      matchAll: 'Dopasuj wszystko',
      matchAny: 'Dopasuj dowolne',
      addRule: 'Dodaj regułę',
      removeRule: 'Usuń regułę',
      // Tłumaczenia dla przycisków
      choose: 'Wybierz',
      upload: 'Prześlij',
      cancel: 'Anuluj',
      // Tłumaczenia dla dat
      dayNames: ["Niedziela", "Poniedziałek", "Wtorek", "Środa", "Czwartek", "Piątek", "Sobota"],
      dayNamesShort: ["Niedz.", "Pon.", "Wt.", "Śr.", "Czw.", "Pt.", "Sob."],
      dayNamesMin: ["N", "Pn", "Wt", "Śr", "Cz", "Pt", "Sb"],
      monthNames: ["Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec", "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"],
      monthNamesShort: ["Sty", "Lut", "Mar", "Kwi", "Maj", "Cze", "Lip", "Sie", "Wrz", "Paź", "Lis", "Gru"],
      dateFormat: 'dd/mm/yy',
      firstDayOfWeek: 1,
      today: 'Dzisiaj',
      weekHeader: 'Tydz',
      // Tłumaczenia dla siły hasła
      weak: 'Słabe',
      medium: 'Średnie',
      strong: 'Mocne',
      passwordPrompt: 'Wprowadź hasło',
      // Tłumaczenia dla komunikatów
      emptyMessage: 'Brak wyników',
      emptyFilterMessage: 'Brak wyników dla filtra',
      pending: 'Oczekuje',
      chooseYear: 'Wybierz rok',
      chooseMonth: 'Wybierz miesiąc',
      chooseDate: 'Wybierz datę',
      prevDecade: 'Poprzednia dekada',
      nextDecade: 'Następna dekada',
      prevYear: 'Poprzedni rok',
      nextYear: 'Następny rok',
      prevMonth: 'Poprzedni miesiąc',
      nextMonth: 'Następny miesiąc',
      prevHour: 'Poprzednia godzina',
      nextHour: 'Następna godzina',
      prevMinute: 'Poprzednia minuta',
      nextMinute: 'Następna minuta',
      prevSecond: 'Poprzednia sekunda',
      nextSecond: 'Następna sekunda',
      am: 'AM',
      pm: 'PM',
      fileSizeTypes: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
      searchMessage: 'Dostępnych wyników: {0}',
      selectionMessage: 'Wybrane elementy: {0}',
      emptySelectionMessage: 'Brak wybranych elementów',
      emptySearchMessage: 'Brak wyników wyszukiwania',
      aria: {
        trueLabel: 'Prawda',
        falseLabel: 'Fałsz',
        nullLabel: 'Nie wybrano',
        star: '1 gwiazdka',
        stars: '{star} gwiazdki',
        selectAll: 'Wybierz wszystkie',
        unselectAll: 'Odznacz wszystkie',
        close: 'Zamknij',
        previous: 'Poprzedni',
        next: 'Następny',
        navigation: 'Nawigacja',
        scrollTop: 'Przewiń do góry',
        moveTop: 'Przenieś na górę',
        moveUp: 'Przenieś wyżej',
        moveDown: 'Przenieś niżej',
        moveBottom: 'Przenieś na dół',
        moveToTarget: 'Przenieś do celu',
        moveToSource: 'Przenieś do źródła',
        moveAllToTarget: 'Przenieś wszystko do celu',
        moveAllToSource: 'Przenieś wszystko do źródła',
        pageLabel: 'Strona {page}',
        firstPageLabel: 'Pierwsza strona',
        lastPageLabel: 'Ostatnia strona',
        nextPageLabel: 'Następna strona',
        prevPageLabel: 'Poprzednia strona',
        rowsPerPageLabel: 'Wierszy na stronę',
        jumpToPageDropdownLabel: 'Skocz do strony',
        jumpToPageInputLabel: 'Skocz do strony',
        selectRow: 'Wybierz wiersz',
        unselectRow: 'Odznacz wiersz',
        expandRow: 'Rozwiń wiersz',
        collapseRow: 'Zwiń wiersz',
        showFilterMenu: 'Pokaż menu filtrów',
        hideFilterMenu: 'Ukryj menu filtrów',
        filterOperator: 'Operator filtru',
        filterConstraint: 'Ograniczenie filtru',
        editRow: 'Edytuj wiersz',
        saveEdit: 'Zapisz edycję',
        cancelEdit: 'Anuluj edycję',
        listView: 'Widok listy',
        gridView: 'Widok siatki',
        slide: 'Pokaz slajdów',
        slideNumber: 'Slajd {slideNumber}',
        zoomImage: 'Powiększ obraz',
        zoomIn: 'Powiększ',
        zoomOut: 'Pomniejsz',
        rotateRight: 'Obróć w prawo',
        rotateLeft: 'Obróć w lewo'
      }
    });
  }
}
